import { capitalize } from "lodash";
import {
  AlertType,
  IAnalysisAlertItem,
  IConfiguredAlertItem,
  ISubscriptionErrors,
  ISubscriptionItem,
  ISubscriptionItemClassificationChange,
  ISubscriptionItemSampleAvailable,
  ISubscriptionItemYaraMatch
} from "../@types";
import {
  ALERT_TYPES_MAP,
  ANALYSIS_ALERT_ITEMS,
  EVENT_TYPE_GROUPS
} from "../const";
import { useAlertSubscriptionDefaults, useSubscriptionHashes } from "./index";
import { formatHashLabel } from "../utils";

const useAlertFormUtilities = (
  values: IConfiguredAlertItem,
  onValues: (values: IConfiguredAlertItem) => void,
  errors?: ISubscriptionErrors[]
) => {
  const { subscriptionHashes, shouldUseHashes } = useSubscriptionHashes();
  const { defaultSubscriptions } = useAlertSubscriptionDefaults({
    shouldUseHashes,
    subscriptionHashes
  });

  const isSubscriptionActive = (
    subscriptions: Array<ISubscriptionItem>,
    subscriptionType: string | string[]
  ): boolean => {
    const types = Array.isArray(subscriptionType)
      ? subscriptionType
      : [subscriptionType];

    return subscriptions.some(subscription =>
      types.includes(subscription.type)
    );
  };

  const getSubscriptionItem = (
    subscriptionType: AlertType
  ): ISubscriptionItem | undefined => {
    return values.subscriptions.find(
      subscription => subscription.type === subscriptionType
    );
  };

  const getSubscriptionErrors = (subscriptionType: AlertType) => {
    const subIndex = getSubscriptionIndex(subscriptionType);
    return errors?.[subIndex] ?? {};
  };

  const handleSubscriptionSelection = (
    subscriptionType: string | string[],
    overrideSelection?: boolean
  ) => {
    const eventTypes = Array.isArray(subscriptionType)
      ? subscriptionType
      : [subscriptionType];

    const finalSubscriptions = eventTypes.reduce(
      (currentSubscriptions, type) => {
        const newSelectionState =
          overrideSelection ??
          !isSubscriptionActive(currentSubscriptions, type);

        if (newSelectionState) {
          const newSubscription = defaultSubscriptions.find(
            sub => sub.type === type
          );

          if (newSubscription) {
            return [...currentSubscriptions, newSubscription];
          }
          return currentSubscriptions;
        } else {
          return currentSubscriptions.filter(
            subscription => subscription.type !== type
          );
        }
      },
      values.subscriptions
    );
    onValues({
      ...values,
      subscriptions: finalSubscriptions
    });
  };

  const getSubscriptionIndex = (type: AlertType): number => {
    return values.subscriptions.findIndex(sub => sub.type === type);
  };

  const handleSubscriptionUpdate = (
    eventType: AlertType,
    updatedSubscription: ISubscriptionItem
  ) => {
    const newSubscriptions = [...values.subscriptions];
    const index = getSubscriptionIndex(eventType);
    newSubscriptions[index] = updatedSubscription;
    onValues({
      ...values,
      subscriptions: newSubscriptions
    });
  };

  const getAnalysisAlertsLabel = (): string[] => {
    const activeAnalysisSubscriptions: string[] = values.subscriptions
      .filter(sub =>
        EVENT_TYPE_GROUPS.ANALYSIS_ALERTS.alert_types.includes(sub.type)
      )
      .map(sub => sub.type);

    return Object.values(ANALYSIS_ALERT_ITEMS)
      .filter(alertItem =>
        alertItem.children.some(childItem =>
          activeAnalysisSubscriptions.includes(childItem.value)
        )
      )
      .map((item: IAnalysisAlertItem) => {
        const childItems = item.children
          .filter(child => activeAnalysisSubscriptions.includes(child.value))
          .map(child => child.label);
        return `${item.label} (${childItems.join(", ")})`;
      });
  };

  const getSampleAvailableLabel = (): string[] => {
    const sampleAvailableSubscription = getSubscriptionItem(
      ALERT_TYPES_MAP.SAMPLE_AVAILABLE
    ) as ISubscriptionItemSampleAvailable;

    const {
      type_options: { fetch_and_analyze: fetchAndAnalyze = false } = {},
      hash_values: hashes = []
    } = sampleAvailableSubscription ?? {};

    const hashCount = hashes.length;
    const hashPreviewCount = 10;

    if (!sampleAvailableSubscription || hashCount === 0) return [];

    const hashPreview = hashes
      .slice(0, hashPreviewCount)
      .map(hash => formatHashLabel(hash))
      .join(", ");

    const label = `${
      fetchAndAnalyze ? "Fetch and Analyze " : ""
    }Hashes: ${hashPreview} (${hashCount} Hash${hashCount > 1 ? "es" : ""})`;

    return [label];
  };

  const getClassificationChangeCardLabel = (): string[] => {
    const classificationChangeSubscription = getSubscriptionItem(
      ALERT_TYPES_MAP.CLASSIFICATION_CHANGE
    ) as ISubscriptionItemClassificationChange;

    if (!classificationChangeSubscription) return [];

    const {
      type_options: classificationChangeOption,
      hash_specific: hashSpecific,
      hash_values: hashes = []
    } = classificationChangeSubscription ?? {};

    const hashCount = hashes.length;

    const label: string[] = [];

    if (
      classificationChangeOption.from_threat_status.length &&
      classificationChangeOption.threat_status.length
    ) {
      const fromStatus = classificationChangeOption.from_threat_status
        .map(status => capitalize(status))
        .join(", ");
      const toStatus = classificationChangeOption.threat_status
        .map(status => capitalize(status))
        .join(", ");
      label.push(`Changes from ${fromStatus} to ${toStatus}`);
    }

    if (classificationChangeOption.threat_level_change) {
      label.push("Risk Score Change");
    }

    if (classificationChangeOption.threat_name_change) {
      label.push("Threat Name Change");
    }

    if (hashSpecific && hashCount > 0) {
      label.push(
        `Hash Specific (${hashCount} Hash${hashCount > 1 ? "es" : ""})`
      );
    }

    return label;
  };

  const getYaraMatchesCardLabel = (): string[] => {
    const yaraSubscription = getSubscriptionItem(
      ALERT_TYPES_MAP.YARA_MATCH
    ) as ISubscriptionItemYaraMatch;

    return yaraSubscription?.type_options?.rulesets ?? [];
  };

  const disableSave: boolean =
    Boolean(!values.name.trim().length) || values.subscriptions.length === 0;

  return {
    isSubscriptionActive,
    getSubscriptionItem,
    getSubscriptionErrors,
    handleSubscriptionSelection,
    handleSubscriptionUpdate,
    getAnalysisAlertsLabel,
    getClassificationChangeCardLabel,
    getSampleAvailableLabel,
    getYaraMatchesCardLabel,
    disableSave
  };
};

export default useAlertFormUtilities;
